import React, { ReactNode } from 'react'

import { Box, Button, Icon } from '@factorialco/deprecated-design-system'

import PopoverMenu from 'modules/core/components/PopoverMenu'

export interface Action {
  icon?: React.ComponentProps<typeof Icon.Medium>['icon']
  label: string
  onClick?: () => void
  to?: string
  disabled?: boolean
}

export interface Option {
  title: string
  description?: string
  icon?: React.ComponentProps<typeof Icon.Medium>['icon']
  onClick?: () => void
  to?: string
  href?: React.ComponentProps<typeof PopoverMenu.HrefOption>['href']
  download?: React.ComponentProps<typeof PopoverMenu.HrefOption>['download']
  target?: React.ComponentProps<typeof PopoverMenu.HrefOption>['target']
  danger?: boolean
}

interface Props {
  options?: Option[]
  optionsButton?: ReactNode
  action?: Action
}

interface ButtonTypeProps {
  action: Action
}

function ButtonType({ action, children }: React.PropsWithChildren<ButtonTypeProps>) {
  if (action.to) {
    return (
      <Button.Router
        to={action.to}
        disabled={action.disabled}
        onClick={action.onClick}
        iconName={action.icon}
      >
        {children as string}
      </Button.Router>
    )
  }

  return (
    <Button onClick={action.onClick} disabled={action.disabled} iconName={action.icon}>
      {children as string}
    </Button>
  )
}

function OptionType({ item }: { item: Option }) {
  if (item.to) {
    return (
      <PopoverMenu.RouterOption
        title={item.title}
        description={item.description}
        to={{ pathname: item.to }}
        onClick={item.onClick}
      />
    )
  }

  if (item.href) {
    return (
      <PopoverMenu.HrefOption
        title={item.title}
        description={item.description}
        href={item.href}
        target={item.target || '_blank'}
        danger={item.danger}
        download={item.download}
      />
    )
  }

  return (
    <PopoverMenu.TouchableOption
      title={item.title}
      description={item.description}
      onClick={item.onClick}
      danger={item.danger}
    />
  )
}

interface OptionsButtonContentProp {
  optionsButton?: ReactNode
}

const OptionsButtonContent = ({ optionsButton }: OptionsButtonContentProp) => {
  if (optionsButton) {
    return <>{optionsButton}</>
  }

  return <Button.Decorative type="secondary" iconName="ellipsis" />
}

function Options({ options, optionsButton }: { options: Option[]; optionsButton?: ReactNode }) {
  if (!options.length) return null

  return (
    <PopoverMenu type="list">
      <PopoverMenu.Trigger>
        <OptionsButtonContent optionsButton={optionsButton} />
      </PopoverMenu.Trigger>
      <PopoverMenu.Content side="bottom" align="center" hideWhenDetached={false}>
        {options.map((item: Option, index: number) => {
          return <OptionType item={item} key={index} />
        })}
      </PopoverMenu.Content>
    </PopoverMenu>
  )
}

function Actions({ action, options, optionsButton }: Props) {
  if (!action && !options) return null

  return (
    <Box>
      {action && <ButtonType action={action}>{action.label}</ButtonType>}
      {options && <Options options={options} optionsButton={optionsButton} />}
    </Box>
  )
}

export default Actions
